<script setup lang="ts">
import getDeliveryPlanVehicleInformation from '@/types/responses/getDeliveryPlanVehicleInformation';
import RegisterModal from '@/components/DailyReportEdit/Combine/RegisterModal.vue';
import combineDailyReportsResponse from 'src/types/responses/combineDailyReports';
import CompleteModal from '@/components/DailyReportEdit/Split/CompleteModal.vue';
import ButtonArea from '@/components/DailyReportEdit/Combine/ButtonArea.vue';
import RightPane from '@/components/DailyReportEdit/Combine/RightPane.vue';
import ArrowIcon from '@/components/DailyReportEdit/Combine/ArrowIcon.vue';
import LeftPane from '@/components/DailyReportEdit/Combine/LeftPane.vue';
import { useAuth } from '@/services/user/Auth0UserProvider';
import Api from '@/services/api/ApiServiceFabrick';
import DeliveryPlan from "@/types/DeliveryPlan";
import { useRouter } from 'vue-router';
import { ref, computed } from 'vue';
import i18n from '@/lang/i18n';

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => void
  deliveryPlan: getDeliveryPlanVehicleInformation
}>();

const { loginWithRedirect } = useAuth();
const router = useRouter();
const selectedDeliveryPlans = ref<(DeliveryPlan | getDeliveryPlanVehicleInformation)[]>([props.deliveryPlan]);
const isOpenModal = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const isDisplayCompleteModal = ref<boolean>(false);
const hasError = ref<boolean>(false);

const isConfirmBtnDisabled = computed(() => {
  return selectedDeliveryPlans.value.length === 1 || hasError.value;
});

function execCombine() {
  isLoading.value = true;
  Api().combineDailyReports(props.deliveryPlan.delivery_plan_id, makeRequest()).then((data: combineDailyReportsResponse) => {
    if (data.status) {
      return isDisplayCompleteModal.value = true;
    }
    hasError.value = true;
    props.showFlash(i18n.global.t('pages.dailyReports.splitForm.failedMessage'), 'error', true);
  }).catch((error) => {
    errorHandler(error);
  }).finally(() => {
    isLoading.value = false;
  });
}

function makeRequest(): any {
  let deliveryPlanIds: string[] = [];
  selectedDeliveryPlans.value.forEach((plan) => {
    deliveryPlanIds.push(plan.delivery_plan_id);
  });

  return {
    combine_items: deliveryPlanIds
  }
}

function errorHandler(error: any) {
  const res = error.response
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      break
    case 403:
      hasError.value = true;
      props.showFlash(res.data.message, 'error', true);
      break
    case 422:
      hasError.value = true;
      props.showFlash(res.data.errors.delivery_plan_id.join(', '), 'error', true);
      break
  }
}

function moveDailyReportDisplay() {
  router.push({ name: "DailyReport" })
}
</script>
<template>
  <div class="content p-4 w-100">
    <v-card class="p-4">
      <h4 class="card-title mt-0 mb-4">
        {{ i18n.global.t('pages.dailyReports.combineForm.title') }}
      </h4>
      <div
        class="w-100 d-flex justify-content-between"
      >
        <div class="w-75 ml-4">
          <LeftPane
            v-model="selectedDeliveryPlans"
            :deliveryPlan="props.deliveryPlan"
          />
        </div>
        <ArrowIcon />
        <div class="w-75 mr-4">
          <RightPane
            v-model="selectedDeliveryPlans"
            :deliveryPlan="props.deliveryPlan"
          />
          <ButtonArea
            v-model="isOpenModal"
            :isDisabled="isConfirmBtnDisabled"
          />
        </div>
      </div>
    </v-card>
  </div>
  <RegisterModal
    v-model="isOpenModal"
    :title="i18n.global.t('pages.dailyReports.combineForm.registerModal.title')"
    :combineItems="{
      driverName: props.deliveryPlan.driver.full_name,
      start: selectedDeliveryPlans[0].leaving_warehouse_date_time,
      end: selectedDeliveryPlans[selectedDeliveryPlans.length - 1].returning_warehouse_date_time,
    }"
    :execCombine="execCombine"
  />
  <CompleteModal
    v-model="isDisplayCompleteModal"
    :messages="[
      i18n.global.t('pages.dailyReports.combineForm.completeModal.messageRow1'),
      i18n.global.t('pages.dailyReports.combineForm.completeModal.messageRow2')
    ]"
    :moveDailyReportDisplay="moveDailyReportDisplay"
  />
</template>
